<template>
  <div class="bryok-modern">
    <div v-if="!signed" class="document-container">
      <!-- Document Preview Section -->
      <div class="preview-section">
        <div class="preview-header">
          <h3 class="preview-title">
            <i class="fas fa-file-alt mr-2"></i>
            Visualização do Documento
          </h3>
          <p class="preview-subtitle">
            Revise o documento antes de assinar digitalmente
          </p>
        </div>

        <div class="iframe-container">
          <div v-if="loading" class="loading-overlay">
            <div class="loading-content">
              <div class="loading-spinner"></div>
              <span class="loading-text">Carregando documento...</span>
            </div>
          </div>

          <iframe
            :src="url"
            :width="width"
            :height="height"
            :frameborder="frameborder"
            :allowfullscreen="allowfullscreen"
            @load="onLoad"
            ref="iframe"
            class="document-iframe"
          />
        </div>
      </div>

      <!-- Action Section -->
      <div class="action-section">
        <div class="action-content">
          <div class="signature-info">
            <div class="info-icon">
              <i class="fas fa-shield-alt"></i>
            </div>
            <div class="info-text">
              <h4 class="info-title">Assinatura Digital Segura</h4>
              <p class="info-description">
                O documento será assinado digitalmente usando certificado válido
              </p>
            </div>
          </div>

          <button
            :disabled="loading"
            @click="execute"
            class="btn-sign-modern"
            type="button"
          >
            <i class="fas fa-signature btn-icon"></i>
            <span class="btn-text">
              {{ loading ? "Processando..." : "Assinar Documento" }}
            </span>
            <div v-if="loading" class="btn-spinner"></div>
          </button>
        </div>
      </div>
    </div>

    <!-- Success State -->
    <div v-if="signed" class="success-container">
      <div class="success-content">
        <div class="success-icon">
          <i class="fas fa-check-circle"></i>
        </div>
        <h2 class="success-title">Documento Assinado!</h2>
        <p class="success-message">
          O documento foi assinado digitalmente com sucesso
        </p>
        <div class="success-badge">
          <i class="fas fa-certificate mr-2"></i>
          Assinatura Verificada
        </div>
      </div>
    </div>

    <!-- Error Messages -->
    <div class="errors-container">
      <ErrorsMessages :errors="errors" />
    </div>
  </div>
</template>

<script>
import ErrorsMessages from "../templates/Components/ErrorsMessages";

import api from "../services/api";

import { numbersOnly, stringsOnly } from "../lib/format";

import html2canvas from "html2canvas";

import jsPDF from "jspdf";

export default {
  name: "BRyOk",

  components: {
    ErrorsMessages,
  },

  data() {
    return {
      signed: false,
      code: null,
      error: null,
      state: null,
      table_id: null,
      table_name: null,
      url: null,
      width: "100%",
      height: "800px",
      frameborder: 1,
      allowfullscreen: true,
      loading: true,
      errors: [],
    };
  },

  created() {
    this.retrieve();
  },

  methods: {
    retrieve() {
      let { code, error, state, signed } = this.$router.history.current.query;
      this.code = code;
      this.error = error;
      this.state = state;
      this.signed = signed;

      let url = null;
      let table_id = numbersOnly(state);
      let table_name = stringsOnly(state);

      this.table_id = table_id;
      this.table_name = table_name;

      const prod = false;

      let baseURL = prod
        ? "https://portal.sistemaclin.com.br"
        : "http://localhost:8080";

      if (table_name == "evolution") {
        url = `${baseURL}/print/evolution/${table_id}?certificate=true`;
      } else if (table_name == "group_prescription_medicine") {
        url = `${baseURL}/prescription/print/${table_id}?certificate=true`;
      } else if (table_name == "semiology_form_question_answer") {
        let tableIds = String(state).split(",");
        let table_ids = [];

        if (tableIds.length == 3) {
          for (let tableId of tableIds) {
            table_ids.push(numbersOnly(tableId));
          }

          let resident_id = table_ids[0];
          let semiology_form_id = table_ids[1];
          let semiology_form_question_answer_id = table_ids[2];
          url = `${baseURL}/print/semiology/${resident_id}/${semiology_form_id}/${semiology_form_question_answer_id}?certificate=true`;

          this.state = `${semiology_form_question_answer_id}semiology_form_question_answer`;
        }
      }

      this.url = url;
    },

    onLoad() {
      this.loading = false;
      this.$emit("loaded");
    },

    async generatePDF() {
      try {
        await this.waitForIframeLoad();

        const iframe = this.$refs.iframe;

        const iframeDoc =
          iframe.contentDocument || iframe.contentWindow.document;

        if (iframeDoc && iframeDoc.body) {
          return await this.generatePDFFromDocument(iframeDoc);
        }
      } catch (error) {
        return {
          error: true,
          message: `Erro ao gerar PDF: ${error.message}. Use a impressão via backend.`,
          base64: null,
          type: "pdf",
        };
      }
    },

    async generatePDFFromDocument(iframeDoc) {
      const elementsToHide = iframeDoc.querySelectorAll(
        'img[src*="http"], iframe, object, embed'
      );
      const originalDisplay = [];

      elementsToHide.forEach((el, index) => {
        originalDisplay[index] = el.style.display;
        el.style.display = "none";
      });

      try {
        const canvas = await html2canvas(iframeDoc.body, {
          margin: 0,
          // useCORS: false,
          allowTaint: false, // Importante: false para evitar tainted canvas
          foreignObjectRendering: false,
          scale: 1,
          backgroundColor: "white",
          logging: false,
          ignoreElements: (element) => {
            const tagName = element.tagName.toLowerCase();
            return (
              ["img", "iframe", "object", "embed", "video"].includes(tagName) &&
              element.src &&
              element.src.startsWith("http") &&
              !element.src.includes(window.location.hostname)
            );
          },
        });

        return await this.createPDFFromCanvas(canvas);
      } finally {
        elementsToHide.forEach((el, index) => {
          el.style.display = originalDisplay[index];
        });
      }
    },

    async createPDFFromCanvas(canvas) {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: "a4",
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;

      const ratio = Math.min(pdfWidth / canvasWidth, pdfHeight / canvasHeight);
      const imgWidth = canvasWidth * ratio;
      const imgHeight = canvasHeight * ratio;

      const x = 0;
      const y = 0;

      const imgData = canvas.toDataURL("image/jpeg", 0.8);

      pdf.addImage(imgData, "JPEG", x, y, imgWidth, imgHeight);

      const pdfBase64Pure = pdf.output("datauristring").split(",")[1];

      return {
        error: false,
        message: null,
        base64: pdfBase64Pure,
        type: "pdf",
      };
    },

    async downloadPdfFromBase64(base64String, fileName = "documento.pdf") {
      try {
        const cleanBase64 = base64String.replace(
          /^data:application\/pdf;base64,/,
          ""
        );

        const byteCharacters = atob(cleanBase64);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.log(error);
      }
    },

    async waitForIframeLoad() {
      return new Promise((resolve) => {
        const iframe = this.$refs.iframe;

        if (!iframe) {
          setTimeout(resolve, 1000);
          return;
        }

        try {
          const doc = iframe.contentDocument || iframe.contentWindow.document;
          if (doc && doc.readyState === "complete") {
            setTimeout(resolve, 1000);
            return;
          }
        } catch (e) {
          console.log(e);
        }

        iframe.onload = () => {
          setTimeout(resolve, 1500);
        };

        setTimeout(resolve, 6000);
      });
    },

    base64ToFile(base64String, filename = "documento.pdf") {
      const base64 = base64String.replace(/^data:application\/pdf;base64,/, "");

      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      return new File([byteArray], filename, { type: "application/pdf" });
    },

    async execute() {
      try {
        this.errors = [];
        this.error = null;
        this.loading = true;

        const generateResult = await this.generatePDF();

        if (generateResult.error) {
          this.errors = [{ message: generateResult.message }];
          this.loading = false;
          return;
        }

        if (!generateResult.base64) {
          this.errors = [{ message: "PDF não foi gerado corretamente" }];
          this.loading = false;
          return;
        }

        const { code, state } = this;

        const file = this.base64ToFile(generateResult.base64, "documento.pdf");

        const formData = new FormData();
        formData.append("code", code);
        formData.append("state", state);
        formData.append("signData", true);
        formData.append("pdf", file);

        const response = await api.post("/bry/user/sign-data", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        const { error, errors, data_base64 } = response.data;

        this.loading = false;

        if (error) {
          this.errors = errors;
        } else {
          if (data_base64) {
            await this.downloadPdfFromBase64(data_base64);

            let { code, error, state } = this.$router.history.current.query;

            this.$router.replace({
              query: { code, error, state, signed: true },
            });

            location.reload();
          }
        }
      } catch (error) {
        this.loading = false;
        if (error.response && error.response.data) {
          const apiError = error.response.data;
          this.errors = [
            { message: apiError.message || "Erro na API de assinatura" },
          ];
        } else {
          this.errors = [
            {
              message:
                "Erro ao processar documento. Verifique conexão e tente novamente.",
            },
          ];
        }
      }
    },
  },
};
</script>

<style scoped>
.bryok-modern {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 24px;
}

/* Document Container */
.document-container {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 32px;
  align-items: start;
}

/* Preview Section */
.preview-section {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.preview-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
  text-align: center;
}

.preview-title {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.preview-title i {
  color: #0f65b0;
}

.preview-subtitle {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

.iframe-container {
  position: relative;
  background: #f8fafc;
  padding: 16px;
}

.document-iframe {
  width: 100%;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.document-iframe:hover {
  border-color: #0f65b0;
  box-shadow: 0 4px 15px rgba(15, 101, 176, 0.1);
}

/* Loading Overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 8px;
  backdrop-filter: blur(4px);
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 32px;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #0f65b0;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  color: #64748b;
  font-size: 16px;
  font-weight: 500;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Action Section */
.action-section {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: sticky;
  top: 24px;
}

.action-content {
  padding: 32px 24px;
}

.signature-info {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 32px;
  padding: 20px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 12px;
  border: 1px solid #0ea5e9;
}

.info-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
  flex-shrink: 0;
}

.info-text {
  flex: 1;
}

.info-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.info-description {
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
}

/* Sign Button */
.btn-sign-modern {
  width: 100%;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 20px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(15, 101, 176, 0.3);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-transform: none;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.btn-sign-modern:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(15, 101, 176, 0.4);
  background: linear-gradient(135deg, #1e40af 0%, #1d4ed8 100%);
}

.btn-sign-modern:active {
  transform: translateY(0);
}

.btn-sign-modern:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.btn-icon {
  font-size: 18px;
  transition: transform 0.3s ease;
}

.btn-sign-modern:hover:not(:disabled) .btn-icon {
  transform: scale(1.1);
}

.btn-text {
  font-size: 16px;
  font-weight: 600;
}

.btn-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-left: 8px;
}

/* Success Container */
.success-container {
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
}

.success-content {
  background: white;
  border-radius: 20px;
  padding: 48px 32px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.2);
}

.success-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px auto;
  color: white;
  font-size: 32px;
  animation: bounce 0.6s ease-out;
}

@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

.success-title {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 12px 0;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.success-message {
  font-size: 16px;
  color: #64748b;
  margin: 0 0 24px 0;
  line-height: 1.6;
}

.success-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  padding: 12px 24px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 600;
  border: 1px solid #34d399;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Errors Container */
.errors-container {
  max-width: 1200px;
  margin: 24px auto 0 auto;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .document-container {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .action-section {
    position: static;
  }
}

@media (max-width: 768px) {
  .bryok-modern {
    padding: 16px;
  }

  .preview-header {
    padding: 20px 16px;
  }

  .preview-title {
    font-size: 18px;
    flex-direction: column;
    gap: 8px;
  }

  .iframe-container {
    padding: 12px;
  }

  .action-content {
    padding: 24px 16px;
  }

  .signature-info {
    padding: 16px;
  }

  .info-icon {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }

  .btn-sign-modern {
    padding: 16px 20px;
    font-size: 15px;
  }

  .success-content {
    padding: 32px 20px;
  }

  .success-icon {
    width: 64px;
    height: 64px;
    font-size: 24px;
  }

  .success-title {
    font-size: 24px;
  }
}

@media (max-width: 480px) {
  .preview-title {
    font-size: 16px;
  }

  .preview-subtitle {
    font-size: 13px;
  }

  .signature-info {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }

  .info-title {
    font-size: 15px;
  }

  .info-description {
    font-size: 13px;
  }

  .btn-sign-modern {
    flex-direction: column;
    gap: 8px;
    padding: 14px 16px;
  }

  .btn-text {
    font-size: 14px;
  }

  .success-title {
    font-size: 20px;
  }

  .success-message {
    font-size: 14px;
  }

  .success-badge {
    font-size: 12px;
    padding: 8px 16px;
  }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .document-iframe {
    border-width: 3px;
  }

  .btn-sign-modern {
    border: 2px solid #1e40af;
  }

  .success-badge {
    border-width: 2px;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .loading-spinner,
  .btn-spinner {
    animation: none;
  }

  .success-icon {
    animation: none;
  }

  * {
    transition: none !important;
  }
}
</style>